import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ConflictsTab } from '../components/bot/ConflictsTab';
import { IntentsTab } from '../components/bot/IntentsTab';
import { QualityTab } from '../components/bot/QualityTab';
import { useAuth } from '../contexts/AuthContext';
import { importBadge } from '../lib/intentView';
import { useBotRoute } from '../lib/useBotRoute';
import { hubService, type ImportStatus } from '../services/hub.service';

const VIEWS = [
  { key: 'intents', label: 'Intents' },
  { key: 'conflicts', label: 'Conflicts' },
  { key: 'quality', label: 'Quality' },
] as const;

const TONE: Record<'gray' | 'amber' | 'green' | 'red', string> = {
  gray: 'bg-gray-100 text-gray-700',
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-700',
};

/**
 * Bot workspace → Intents (hub-ui-step1 spec U7; NLU P1 plan B1). The view is in the URL (?view=conflicts) so it
 * can be linked. The badge says whether the bot's intents are imported from chatbot and its rollout state.
 */
export function BotIntents() {
  const { system, botId } = useBotRoute();
  const { getToken } = useAuth();
  const [params, setParams] = useSearchParams();
  const raw = params.get('view');
  const view = raw === 'conflicts' || raw === 'quality' ? raw : 'intents';
  const [status, setStatus] = useState<ImportStatus | null>(null);
  const k = `${system}:${botId}`;

  useEffect(() => {
    let alive = true;
    setStatus(null);
    // Informational only: a failure just hides the badge.
    hubService
      .getImportStatus(botId, system, getToken())
      .then((s) => alive && setStatus(s))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [botId, system, getToken]);

  const badge = importBadge(status);
  const isV2 = system === 'chatbot_v2';
  const [useIntents, setUseIntents] = useState<boolean | null>(null);
  const [savingSwitch, setSavingSwitch] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  useEffect(() => {
    if (!isV2) return;
    let alive = true;
    setUseIntents(null);
    hubService
      .getBotRuntimeConfig(system, botId, getToken())
      .then((c) => alive && setUseIntents(c?.intentsEnabled === true))
      .catch(() => alive && setUseIntents(false));
    return () => {
      alive = false;
    };
  }, [isV2, system, botId, getToken]);

  async function toggleUseIntents() {
    if (useIntents === null) return;
    setSavingSwitch(true);
    setSwitchError(null);
    try {
      const c = await hubService.updateBotRuntimeConfig(system, botId, { intentsEnabled: !useIntents }, getToken());
      setUseIntents(c.intentsEnabled === true);
    } catch (e) {
      setSwitchError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingSwitch(false);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">Intents</h1>
          {badge && <span className={`text-xs px-2 py-0.5 rounded ${TONE[badge.tone]}`}>{badge.label}</span>}
          {isV2 && useIntents !== null && (
            <label className="flex items-center gap-2 text-xs text-gray-700" title="Plan C1: hub answers matched typed messages for this v2 bot">
              <input type="checkbox" checked={useIntents} disabled={savingSwitch} onChange={toggleUseIntents} />
              <span className="font-medium">Use intents</span>
              <span className="text-gray-500">{useIntents ? 'On — matched intents answer' : 'Off — v2 answers as before'}</span>
            </label>
          )}
          {switchError && <span className="text-xs text-red-600">{switchError}</span>}
          {status?.lastSyncedAt && (
            <span className="text-xs text-gray-500">last sync {new Date(status.lastSyncedAt).toLocaleString()}</span>
          )}
        </div>
        <div role="tablist" className="inline-flex overflow-hidden rounded border border-gray-300 text-sm">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              role="tab"
              aria-selected={view === v.key}
              onClick={() => setParams(v.key === 'intents' ? {} : { view: v.key }, { replace: true })}
              className={`px-3 py-1 ${view === v.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>
      {view === 'conflicts' ? (
        <ConflictsTab key={k} botId={botId} system={system} />
      ) : view === 'quality' ? (
        <QualityTab key={k} botId={botId} system={system} />
      ) : (
        <IntentsTab key={k} botId={botId} system={system} />
      )}
    </div>
  );
}
